import './ReportShelf.scss';

import { Button } from '@trackman/web-shared-components';
import { ChartType } from 'types/CodegenReportTypes';
import { useReportId } from 'hooks/helperHooks';
import { useTranslation } from 'react-i18next';
import { useTelemetry } from 'hooks/useTelemetry';
import { useReportViewModeStore } from 'stores/ReportViewModeStore';
import EditLayoutActions from 'components/Report/EditLayoutActions/EditLayoutActions';

type Props = {
  children: React.ReactNode;
  onGenerate: () => void;
  onModify: () => void;
  isLoadingReport: boolean;
  disableGenerate: boolean;
  editingChart: ChartType | undefined;
  setEditingChart: (chart: ChartType | undefined) => void;
  isEditMode: boolean;
  setIsEditMode: (value: boolean) => void;
  saveLayoutForSession: () => void;
  saveLayoutForOrg: () => void;
  hasUnsavedEdits: boolean;
  discardLayoutChanges: () => void;
};

const ReportShelf = ({
  children,
  onGenerate,
  onModify,
  isLoadingReport,
  disableGenerate,
  editingChart,
  setEditingChart,
  isEditMode,
  setIsEditMode,
  saveLayoutForSession,
  saveLayoutForOrg,
  hasUnsavedEdits,
  discardLayoutChanges,
}: Props) => {
  const { t } = useTranslation();
  const { isCreate } = useReportId();
  const { viewMode, showShelfForSingleViewMode, setShowShelfForSingleViewMode } = useReportViewModeStore();
  const { trackEvent, events } = useTelemetry();

  function handleClick(): void {
    if (editingChart) {
      setEditingChart(undefined);
      trackEvent(events.EditedReportLayout);
    } else if (isCreate) {
      onGenerate();
      setShowShelfForSingleViewMode(false);
      trackEvent(events.GeneratedReport);
    } else {
      onModify();
      setShowShelfForSingleViewMode(false);
      trackEvent(events.AdjustedReportContent);
    }
  }

  return (
    <div className={`ReportShelf-container ${viewMode === 'single' && !showShelfForSingleViewMode ? 'hidden' : ''}`}>
      {children}
      <div className='ReportShelf-buttons'>
        {!isEditMode && (
          <Button
            key='apply-button'
            label={isCreate ? t('Generate') : t('Apply')}
            intent='primary'
            onClick={handleClick}
            style={{ width: '100%' }}
            disabled={isCreate ? disableGenerate : isLoadingReport}
            isLoading={isLoadingReport}
          />
        )}

        {isEditMode && editingChart && (
          <Button
            key='done-button'
            label={t('Done')}
            intent='primary'
            onClick={handleClick}
            style={{ width: '100%' }}
            disabled={false}
            isLoading={false}
          />
        )}

        {isEditMode && !editingChart && (
          <EditLayoutActions
            setIsEditMode={setIsEditMode}
            saveLayoutForSession={saveLayoutForSession}
            saveLayoutForOrg={saveLayoutForOrg}
            hasUnsavedEdits={hasUnsavedEdits}
            discardLayoutChanges={discardLayoutChanges}
          />
        )}
      </div>
    </div>
  );
};

export default ReportShelf;
